import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  ArrowDownToLine,
  FileSpreadsheet,
  Plus,
  X,
  ChevronUp,
  ChevronDown,
  Pencil,
  Trash2,
  Search,
} from "lucide-react";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import axios from "axios";
import { QRCodeCanvas } from "qrcode.react";
import { getToken, base_url } from "../../config/config";
import ANRModal from "../../scenes/Aufgabe/KanbanComponents/ANRModal";
import { toNumber } from "lodash";

// ---------- Helper functions ----------

/** Parses multiple date string formats into a Date object */
const parseDate = (dateStr) => {
  if (!dateStr) return new Date();
  const separator = dateStr.includes("-") ? "-" : ".";
  const parts = dateStr.split(separator);
  if (parts[0].length === 4) {
    const [year, month, day] = parts;
    return new Date(year, month - 1, day);
  } else {
    const [day, month, year] = parts;
    return new Date(year, month - 1, day);
  }
};

/** Formats a date string as "dd.MM.yyyy" */
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const dateObj = parseDate(dateStr);
  const day = dateObj.getDate().toString().padStart(2, "0");
  const month = (dateObj.getMonth() + 1).toString().padStart(2, "0");
  const year = dateObj.getFullYear();
  return `${day}.${month}.${year}`;
};

/** Formats invoice ID with year prefix (MR_YY-) */
const formatInvoiceNumber = (id, dateStr) => {
  if (!id) return "";
  if (!dateStr) return id;

  try {
    const dateObj = parseDate(dateStr);
    const year = dateObj.getFullYear();
    const yearSuffix = year.toString().slice(-2); // Get last 2 digits
    return `MR_${yearSuffix}-${id}`;
  } catch (error) {
    console.warn("Error formatting invoice number:", error);
    return id;
  }
};

function Rechnungs() {
  // ------------------ State Initialization ------------------
  const [mergedData, setMergedData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialog states
  const [openDialog, setOpenDialog] = useState(false); // Add item dialog
  const [openViewDialog, setOpenViewDialog] = useState(false); // View items
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [SelectedInvoiceAmount, setSelectedInvoiceAmount] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);

  // "Add new item" states
  const [selectedItem, setSelectedItem] = useState(null);
  const [newItem, setNewItem] = useState({
    position: "",
    singleStuckCost: "",
    numberOfStuck: "",
    totalStuckCost: "",
    ANR: "",
    percentage: 100,
    percentage2: "",
    description: "",
    totalTon: "",
    costPerTon: "",
    Abmessang: [],
    Wandstärke: "",
    Material1: "",
    Material2: "",
    Material3: "",
    Material4: "",
    bestullungNr: "",
    rechnuunglefrant: "",
    aufragNr: "",
    type: "Bestellung",
  });
  const [abmessungEntries, setAbmessungEntries] = useState([]);

  // Validation / feedback for ANR
  const [anrValid, setAnrValid] = useState(null);
  const [anrMessage, setAnrMessage] = useState("");

  // Material/cost type options
  const [materialArtOptions, setMaterialArtOptions] = useState([]);
  const [kostenArtOptions, setKostenArtOptions] = useState([]);

  // Next material sequence (if you use it in the Add Item dialog)
  const [materialSequenceNumber, setMaterialSequenceNumber] = useState(null);

  // For filtering columns
  const [columnFilters, setColumnFilters] = useState({
    id: "",
    status: "",
    Konto: "",
    Datum: "",
    customerByAbdul: "",
    Beleg1: "",
    Soll: "",
    anr: "",
  });

  // For sorting
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const isCalculating = useRef(false);

  // ------------------ Invoice Files & Upload ------------------
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [uploadInvoiceId, setUploadInvoiceId] = useState(null);
  const [file, setFile] = useState(null);

  // For viewing all invoice files in a modal
  const [openInvoiceFilesDialog, setOpenInvoiceFilesDialog] = useState(false);
  const [selectedInvoiceFiles, setSelectedInvoiceFiles] = useState([]);

  // ------------------ QR Code Modal ------------------
  const [openQrModal, setOpenQrModal] = useState(false);
  const [qrValue, setQrValue] = useState("");

  // ------------------ ANR Modal ------------------
  const [openAnrModal, setOpenAnrModal] = useState(false);

  // ------------------ Image Preview ------------------
  const [selectedImage, setSelectedImage] = useState(null);

  // ------------------ Delete Confirmation ------------------
  const [showDeleteConfirmRechnung, setShowDeleteConfirmRechnung] =
    useState(false);
  const [deleteRechnungId, setDeleteRechnungId] = useState(null);

  // ------------------ Add Rechnung Modal ------------------
  const [openAddRechnungModal, setOpenAddRechnungModal] = useState(false);
  const [newRechnung, setNewRechnung] = useState({
    id: "",
    Konto: "",
    Datum: new Date().toISOString().split("T")[0],
    Gkto: "",
    customerName: "",
    Beleg1: "",
    Soll: "",
    skonto: "",
    skontoPercentage: "",
    discountedAmount: "",
    file: null,
    Buchungstext: "",
    AN: "",
    isLieferant: false,
    lieferantId: "",
    lieferantName: "",
    lieferantKonto: "",
    kundeId: "",
  });
  const [isCreatingRechnung, setIsCreatingRechnung] = useState(false);

  // Customer/Kunde data
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [companySearchTerm, setCompanySearchTerm] = useState("");
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);

  // Supplier/Lieferant data
  const [lieferanten, setLieferanten] = useState([]);
  const [loadingLieferanten, setLoadingLieferanten] = useState(false);
  const [filteredLieferanten, setFilteredLieferanten] = useState([]);
  const [lieferantSearchTerm, setLieferantSearchTerm] = useState("");
  const [showLieferantDropdown, setShowLieferantDropdown] = useState(false);

  // Unified search for both Kunde and Lieferant
  const [unifiedSearchTerm, setUnifiedSearchTerm] = useState("");
  const [unifiedResults, setUnifiedResults] = useState([]);
  const [showUnifiedDropdown, setShowUnifiedDropdown] = useState(false);
  const [isLoadingUnified, setIsLoadingUnified] = useState(false);

  const openImagePreview = (imageUrl) => {
    setSelectedImage(imageUrl);
  };

  const closeImagePreview = () => {
    setSelectedImage(null);
  };

  // Fetch companies and lieferanten when opening the add rechnung modal
  const fetchCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const response = await axios.get(`${base_url}/v1/getKunden`);
      setCompanies(response.data.data || []);
      setFilteredCompanies(response.data.data || []);
    } catch (error) {
      console.error("Error fetching companies:", error);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const fetchLieferanten = async () => {
    setLoadingLieferanten(true);
    try {
      const response = await axios.get(`${base_url}/v1/getLieferant`);
      setLieferanten(response.data.data || []);
      setFilteredLieferanten(response.data.data || []);
    } catch (error) {
      console.error("Error fetching lieferanten:", error);
    } finally {
      setLoadingLieferanten(false);
    }
  };

  const handleCompanySearch = (e) => {
    const searchTerm = e.target.value.toLowerCase();
    setCompanySearchTerm(searchTerm);

    if (searchTerm) {
      const filtered = companies.filter(
        (company) =>
          company.bezeichnung.toLowerCase().includes(searchTerm) ||
          company.konto.includes(searchTerm) ||
          (company.kurzbezeichnung &&
            company.kurzbezeichnung.toLowerCase().includes(searchTerm)),
      );
      setFilteredCompanies(filtered);
      setShowCompanyDropdown(true);
    } else {
      setFilteredCompanies(companies);
      setShowCompanyDropdown(false);
    }
  };

  const handleLieferantSearch = (e) => {
    const searchTerm = e.target.value.toLowerCase();
    setLieferantSearchTerm(searchTerm);

    if (searchTerm) {
      const filtered = lieferanten.filter(
        (lieferant) =>
          lieferant.bezeichnung.toLowerCase().includes(searchTerm) ||
          lieferant.konto.includes(searchTerm) ||
          (lieferant.kurzbezeichnung &&
            lieferant.kurzbezeichnung.toLowerCase().includes(searchTerm)),
      );
      setFilteredLieferanten(filtered);
      setShowLieferantDropdown(true);
    } else {
      setFilteredLieferanten(lieferanten);
      setShowLieferantDropdown(false);
    }
  };

  const handleSelectCompany = (company) => {
    setNewRechnung({
      ...newRechnung,
      Konto: company.konto,
      customerName: company.bezeichnung,
      Buchungstext: company.bezeichnung,
      AN: company.bezeichnung,
      Gkto: company.gkto || "",
      isLieferant: false,
      kundeId: company._id,
      lieferantId: "",
      lieferantName: "",
      lieferantKonto: "",
    });
    setCompanySearchTerm(company.bezeichnung);
    setShowCompanyDropdown(false);
    // Clear lieferant search if company is selected
    setLieferantSearchTerm("");
  };

  const handleSelectLieferant = (lieferant) => {
    setNewRechnung({
      ...newRechnung,
      Konto: lieferant.konto,
      customerName: lieferant.bezeichnung,
      Buchungstext: lieferant.bezeichnung,
      AN: lieferant.bezeichnung,
      Gkto: lieferant.gkto || "",
      isLieferant: true,
      lieferantId: lieferant._id,
      lieferantName: lieferant.bezeichnung,
      lieferantKonto: lieferant.konto,
      kundeId: "",
    });
    setLieferantSearchTerm(lieferant.bezeichnung);
    setShowLieferantDropdown(false);
    // Clear company search if lieferant is selected
    setCompanySearchTerm("");
  };

  // Unified search handler for both Kunde and Lieferant
  const handleUnifiedSearch = (e) => {
    const searchTerm = e.target.value.toLowerCase();
    setUnifiedSearchTerm(searchTerm);

    if (searchTerm) {
      setIsLoadingUnified(true);

      // Filter companies
      const filteredCompanies = companies.filter(
        (company) =>
          company.bezeichnung.toLowerCase().includes(searchTerm) ||
          company.konto.includes(searchTerm) ||
          (company.kurzbezeichnung &&
            company.kurzbezeichnung.toLowerCase().includes(searchTerm)),
      );

      // Filter lieferanten
      const filteredLieferanten = lieferanten.filter(
        (lieferant) =>
          lieferant.bezeichnung.toLowerCase().includes(searchTerm) ||
          lieferant.konto.includes(searchTerm) ||
          (lieferant.kurzbezeichnung &&
            lieferant.kurzbezeichnung.toLowerCase().includes(searchTerm)),
      );

      // Combine results with type indicator
      const combinedResults = [
        ...filteredCompanies.map((item) => ({ ...item, type: "kunde" })),
        ...filteredLieferanten.map((item) => ({ ...item, type: "lieferant" })),
      ];

      setUnifiedResults(combinedResults);
      setShowUnifiedDropdown(true);
      setIsLoadingUnified(false);
    } else {
      setUnifiedResults([]);
      setShowUnifiedDropdown(false);
      setIsLoadingUnified(false);
    }
  };

  // Handle selection from unified dropdown
  const handleUnifiedSelection = (item) => {
    if (item.type === "kunde") {
      setNewRechnung({
        ...newRechnung,
        Konto: item.konto,
        customerName: item.bezeichnung,
        Buchungstext: item.bezeichnung,
        AN: item.bezeichnung,
        Gkto: item.gkto || "",
        isLieferant: false,
        kundeId: item._id,
        lieferantId: "",
        lieferantName: "",
        lieferantKonto: "",
      });
    } else {
      setNewRechnung({
        ...newRechnung,
        Konto: item.konto,
        customerName: item.bezeichnung,
        Buchungstext: item.bezeichnung,
        AN: item.bezeichnung,
        Gkto: item.gkto || "",
        isLieferant: true,
        lieferantId: item._id,
        lieferantName: item.bezeichnung,
        lieferantKonto: item.konto,
        kundeId: "",
      });
    }

    setUnifiedSearchTerm(item.bezeichnung);
    setShowUnifiedDropdown(false);
    // Clear old search terms
    setCompanySearchTerm("");
    setLieferantSearchTerm("");
  };

  const handleOpenAddRechnungModal = () => {
    setOpenAddRechnungModal(true);
    // Reset the form
    setNewRechnung({
      id: "",
      Konto: "",
      Datum: new Date().toISOString().split("T")[0],
      Gkto: "",
      customerName: "",
      Beleg1: "",
      Soll: "",
      file: null,
      Buchungstext: "",
      AN: "",
      isLieferant: false,
      lieferantId: "",
      lieferantName: "",
      lieferantKonto: "",
      kundeId: "",
    });
    setCompanySearchTerm("");
    setLieferantSearchTerm("");
    setUnifiedSearchTerm("");
    setShowUnifiedDropdown(false);
    setUnifiedResults([]);

    // Fetch both companies and lieferanten in parallel
    fetchCompanies();
    fetchLieferanten();
  };

  const handleCloseAddRechnungModal = () => {
    setOpenAddRechnungModal(false);
    // If we're in the middle of creating a rechnung, cancel it
    if (isCreatingRechnung) {
      setIsCreatingRechnung(false);
    }
    // Clear search terms
    setUnifiedSearchTerm("");
    setShowUnifiedDropdown(false);
    setUnifiedResults([]);
    setCompanySearchTerm("");
    setLieferantSearchTerm("");
  };

  const handleRechnungInputChange = (e) => {
    const { name, value } = e.target;

    // Handle number inputs more smoothly
    let processedValue = value;
    if (name === "Soll" || name === "skontoPercentage") {
      // Allow empty string for clearing the field
      if (value === "") {
        processedValue = "";
      } else {
        // Ensure valid number format
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
          processedValue = value; // Keep the raw input for smooth typing
        } else {
          return; // Invalid input, don't update
        }
      }
    }

    setNewRechnung((prev) => {
      const updated = {
        ...prev,
        [name]: processedValue,
      };

      // Calculate SKONTO discount when Betrag (Soll) or skontoPercentage changes
      if (name === "Soll" || name === "skontoPercentage") {
        let originalBetrag, skontoPercentage;

        if (name === "Soll") {
          // When Betrag is changed, store as original amount
          originalBetrag = parseFloat(processedValue) || 0;
          if (originalBetrag > 0) {
            updated.originalBetrag = originalBetrag.toString();
          }
          skontoPercentage = parseFloat(updated.skontoPercentage) || 0;
        } else {
          // When SKONTO percentage is changed, use existing original amount or current Soll
          originalBetrag =
            parseFloat(updated.originalBetrag || updated.Soll) || 0;
          if (!updated.originalBetrag && originalBetrag > 0) {
            updated.originalBetrag = originalBetrag.toString();
          }
          skontoPercentage = parseFloat(processedValue) || 0;
        }

        const skonto = (originalBetrag * skontoPercentage) / 100;
        const discountedAmount = originalBetrag - skonto;

        // Store calculated values with proper precision for display
        updated.skonto = skonto.toFixed(2);
        updated.discountedAmount = discountedAmount.toFixed(2);

        // Update the Betrag field with smooth number formatting
        if (skontoPercentage > 0) {
          updated.Soll = discountedAmount.toString();
        }
      }

      return updated;
    });
  };

  const handleRechnungFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setNewRechnung((prev) => ({
        ...prev,
        file: e.target.files[0],
      }));
    }
  };

  const handleCreateRechnung = async () => {
    // Basic validation
    if (!newRechnung.Konto || !newRechnung.Datum || !newRechnung.Soll) {
      alert("Bitte füllen Sie alle Pflichtfelder aus.");
      return;
    }

    setIsCreatingRechnung(true);

    try {
      // First create the invoice record
      const rechnungData = {
        Konto: newRechnung.Konto,
        Datum: newRechnung.Datum,
        Gkto: newRechnung.Gkto,
        customerName: newRechnung.customerName,
        Beleg1: newRechnung.Beleg1,
        Soll: newRechnung.Soll,
        AN: newRechnung.AN,
        Buchungstext: newRechnung.Buchungstext,

        // Include SKONTO fields
        skontoPercentage: newRechnung.skontoPercentage || null,
        skonto: newRechnung.skonto || null,
        discountedAmount: newRechnung.discountedAmount || null,
        originalBetrag: newRechnung.originalBetrag || null,

        // Include the lieferant/kunde distinction
        isLieferant: newRechnung.isLieferant,
        lieferantId:
          newRechnung.lieferantId !== ""
            ? newRechnung.lieferantId || null
            : null,
        lieferantName: newRechnung.lieferantName,
        lieferantKonto: newRechnung.lieferantKonto,
        kundeId:
          newRechnung.kundeId !== "" ? newRechnung.kundeId || null : null,
      };

      const response = await fetch(`${base_url}/v1/uploadRechnungs`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify([rechnungData]), // API expects array of invoices
      });

      if (!response.ok) {
        throw new Error("Fehler beim Erstellen der Rechnung");
      }

      const result = await response.json();
      const rechnungId = result.invoice?._id || result.data?.id;

      // If we have a file, upload it
      if (newRechnung.file && rechnungId) {
        const formData = new FormData();
        formData.append("invoices", newRechnung.file);

        const uploadResponse = await fetch(
          `${base_url}/v1/updateInvoiceDocument?id=${rechnungId}`,
          {
            method: "POST",
            body: formData,
          },
        );

        if (!uploadResponse.ok) {
          console.warn(
            "Rechnung wurde erstellt, aber die Datei konnte nicht hochgeladen werden.",
          );
        }
      }

      // Refresh the data
      await fetchData();

      // Close the modal and reset state
      handleCloseAddRechnungModal();
      alert("Rechnung erfolgreich erstellt!");
    } catch (error) {
      console.error("Error creating rechnung:", error);
      alert("Fehler beim Erstellen der Rechnung: " + error.message);
    } finally {
      setIsCreatingRechnung(false);
    }
  };

  // ------------------ Delete Rechnung Functions ------------------
  const handleDeleteRechnungClick = (rechnungId) => {
    setDeleteRechnungId(rechnungId);
    setShowDeleteConfirmRechnung(true);
  };

  const handleCancelDeleteRechnung = () => {
    setShowDeleteConfirmRechnung(false);
    setDeleteRechnungId(null);
  };

  const handleConfirmDeleteRechnung = async () => {
    if (!deleteRechnungId) return;

    setIsDeleteLoading(true);
    try {
      const token = getToken();
      const response = await axios.delete(
        `${base_url}/v1/deleteRechnung/${deleteRechnungId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.status === 200) {
        alert("Rechnung erfolgreich gelöscht!");
        await fetchData(); // Refresh the data
        handleCancelDeleteRechnung();
      }
    } catch (error) {
      console.error("Error deleting rechnung:", error);
      alert(
        "Fehler beim Löschen der Rechnung: " +
          (error.response?.data?.message || error.message),
      );
    } finally {
      setIsDeleteLoading(false);
    }
  };

  // ------------------ Sorting Helpers ------------------
  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortValue = (item, key) => {
    if (key === "status") {
      return item.items.length > 0 ? "hat Artikel" : "Keine Artikel";
    }
    return item[key];
  };

  // ------------------ Fetch Material Sequence Number ------------------
  const fetchMaterialSequenceNumber = async () => {
    try {
      const response = await axios.get(
        `${base_url}/v1/getNewMaterialSequenceValue`,
      );
      setMaterialSequenceNumber(response.data[0].seq);
    } catch (error) {
      console.error("Error fetching material sequence number:", error);
    }
  };

  // ------------------ Fetch Initial Data ------------------
  const fetchData = async () => {
    try {
      const rechnungsResponse = await fetch(`${base_url}/v1/getallRechnungs`);
      const rechnungsData = await rechnungsResponse.json();

      const firmaResponse = await fetch(`${base_url}/v1/getFirma`);
      const firmaData = await firmaResponse.json();

      // Merge data
      const merged = rechnungsData.map((rechnung) => {
        const matchingFirma = firmaData.data.find(
          (firma) => firma.konto === rechnung.Gkto,
        );
        const sollPercent = (rechnung.Soll / 1.19).toFixed(2).replace(".", ",");

        return {
          ...rechnung,
          // If the backend includes "invoiceFiles" for each invoice
          invoiceFiles: rechnung.invoiceFiles || [],
          customerByAbdul: matchingFirma ? matchingFirma.bezeichnung : "N/A",
          customerByAgenda: rechnung.Buchungstext,
          sollPercent,
          items: rechnung.items || [],
        };
      });

      fetchMaterialSequenceNumber();
      setMergedData(merged);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // ------------------ Fetch Material Art and Kosten Art ------------------
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const materialArtResponse = await fetch(
          `${base_url}/v1/getAllMaterailart`,
        );
        const kostenArtResponse = await fetch(`${base_url}/v1/getAllKostenart`);

        const materialArtData = await materialArtResponse.json();
        const kostenArtData = await kostenArtResponse.json();

        setMaterialArtOptions(materialArtData?.data || []);
        setKostenArtOptions(kostenArtData?.data || []);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching dropdowns:", error);
        setLoading(false);
      }
    };

    fetchDropdowns();
  }, []);

  // ------------------ Abmessung Handlers (for Add Item) ------------------
  const handleAddAbmessung = () => {
    setAbmessungEntries([
      ...abmessungEntries,
      { name: "", unit: "", value: "" },
    ]);
  };
  const handleRemoveAbmessung = (index) => {
    const updatedEntries = [...abmessungEntries];
    updatedEntries.splice(index, 1);
    setAbmessungEntries(updatedEntries);
  };
  const handleAbmessungChange = (index, field, value) => {
    const updatedEntries = [...abmessungEntries];
    updatedEntries[index][field] = value;
    setAbmessungEntries(updatedEntries);
  };
  const abmessungNames = [
    "Länge",
    "Breite",
    "Höhe",
    "Durchmesser",
    "Radius",
    "Tiefe",
  ];
  const abmessungUnits = ["mm", "cm", "m", "inch", "ft"];

  // ------------------ Material & Kosten Art Handlers (Add Item) ------------------
  const handleMaterialArtChange = async (event, section) => {
    const newMaterialArt = event.target.value;
    if (newMaterialArt === "add-new") {
      const newMaterialName = prompt("Enter new Material Art:");
      if (newMaterialName) {
        try {
          await fetch(`${base_url}/v1/createMaterailart`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ name: newMaterialName }),
          });
          setMaterialArtOptions([
            ...materialArtOptions,
            { name: newMaterialName },
          ]);
          selectedItem
            ? setSelectedItem({ ...selectedItem, [section]: newMaterialArt })
            : setNewItem({ ...newItem, [section]: newMaterialArt });
        } catch (error) {
          console.error("Error adding new material art:", error);
        }
      }
    } else {
      selectedItem
        ? setSelectedItem({ ...selectedItem, [section]: newMaterialArt })
        : setNewItem({ ...newItem, [section]: newMaterialArt });
    }
  };

  const handleKostenArtChange = async (event) => {
    const newKostenArt = event.target.value;
    if (newKostenArt === "add-new") {
      const newKostenName = prompt("Neue Kostenart eingeben:");
      if (newKostenName) {
        try {
          await fetch(`${base_url}/v1/createKostenart`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ name: newKostenName }),
          });
          setKostenArtOptions([...kostenArtOptions, { name: newKostenName }]);
          selectedItem
            ? setSelectedItem({ ...selectedItem, Material1: newKostenArt })
            : setNewItem({ ...newItem, Material1: newKostenArt });
        } catch (error) {
          console.error("Error adding new kosten art:", error);
        }
      }
    } else {
      selectedItem
        ? setSelectedItem({ ...selectedItem, Material1: newKostenArt })
        : setNewItem({ ...newItem, Material1: newKostenArt });
    }
  };

  // ------------------ Percentage Handlers ------------------
  // ------------------ Percentage Handlers ------------------
  const handlePercentageChange = (e) => {
    const inputValue = e.target.value;

    // Handle empty input
    if (inputValue === "") {
      selectedItem
        ? setSelectedItem((prev) => ({
            ...prev,
            percentage: "",
            percentage2: 100,
          }))
        : setNewItem((prev) => ({ ...prev, percentage: "", percentage2: 100 }));
      return;
    }

    let value = parseFloat(inputValue);
    // Clamp 0-100
    if (value < 0) value = 0;
    if (value > 100) value = 100;

    const otherPercentage = parseFloat((100 - value).toFixed(2));

    // Preserve the user's input string if it matches the parsed value (e.g. "50.")
    // unless we clamped it.
    const finalValue = parseFloat(inputValue) === value ? inputValue : value;

    selectedItem
      ? setSelectedItem((prevItem) => ({
          ...prevItem,
          percentage: finalValue,
          percentage2: otherPercentage,
        }))
      : setNewItem((prevItem) => ({
          ...prevItem,
          percentage: finalValue,
          percentage2: otherPercentage,
        }));
  };

  const handlePercentage2Change = (e) => {
    const inputValue = e.target.value;

    // Handle empty input
    if (inputValue === "") {
      selectedItem
        ? setSelectedItem((prev) => ({
            ...prev,
            percentage2: "",
            percentage: 100,
          }))
        : setNewItem((prev) => ({ ...prev, percentage2: "", percentage: 100 }));
      return;
    }

    let value = parseFloat(inputValue);
    if (value < 0) value = 0;
    if (value > 100) value = 100;

    const otherPercentage = parseFloat((100 - value).toFixed(2));
    const finalValue = parseFloat(inputValue) === value ? inputValue : value;

    selectedItem
      ? setSelectedItem((prevItem) => ({
          ...prevItem,
          percentage2: finalValue,
          percentage: otherPercentage,
        }))
      : setNewItem((prevItem) => ({
          ...prevItem,
          percentage2: finalValue,
          percentage: otherPercentage,
        }));
  };

  // ------------------ Cost Calculation Handlers (Add Item) ------------------
  const handleSingleStuckCostChange = (e) => {
    selectedItem
      ? setSelectedItem((prev) => ({
          ...prev,
          singleStuckCost: e.target.value,
        }))
      : setNewItem((prev) => ({ ...prev, singleStuckCost: e.target.value }));
  };
  const handleNumberOfStuckChange = (e) => {
    selectedItem
      ? setSelectedItem((prev) => ({
          ...prev,
          numberOfStuck: e.target.value,
        }))
      : setNewItem((prev) => ({ ...prev, numberOfStuck: e.target.value }));
  };
  const handleTotalStuckCostChange = (e) => {
    selectedItem
      ? setSelectedItem((prev) => ({
          ...prev,
          totalStuckCost: e.target.value,
        }))
      : setNewItem((prev) => ({ ...prev, totalStuckCost: e.target.value }));
  };

  // Whenever singleStuckCost, numberOfStuck, or totalStuckCost change, recalc
  useEffect(() => {
    if (isCalculating.current) {
      isCalculating.current = false;
      return;
    }
    const { singleStuckCost, numberOfStuck, totalStuckCost } = newItem;
    const single = parseFloat(singleStuckCost);
    const number = parseFloat(numberOfStuck);
    const total = parseFloat(totalStuckCost);
    const isSingleValid = !isNaN(single) && singleStuckCost.trim() !== "";
    const isNumberValid = !isNaN(number) && numberOfStuck.trim() !== "";
    const isTotalValid = !isNaN(total) && totalStuckCost.trim() !== "";
    const filledFields = [isSingleValid, isNumberValid, isTotalValid].filter(
      Boolean,
    ).length;
    if (filledFields === 2) {
      if (!isTotalValid) {
        const calculatedTotal = single * number;
        isCalculating.current = true;
        setNewItem((prev) => ({
          ...prev,
          totalStuckCost: calculatedTotal.toFixed(2),
        }));
      } else if (!isSingleValid) {
        if (number !== 0) {
          const calculatedSingle = total / number;
          isCalculating.current = true;
          setNewItem((prev) => ({
            ...prev,
            singleStuckCost: calculatedSingle.toFixed(2),
          }));
        }
      } else if (!isNumberValid) {
        if (single !== 0) {
          const calculatedNumber = total / single;
          isCalculating.current = true;
          setNewItem((prev) => ({
            ...prev,
            numberOfStuck: calculatedNumber.toFixed(2),
          }));
        }
      }
    }
  }, [newItem.singleStuckCost, newItem.numberOfStuck, newItem.totalStuckCost]);

  // ------------------ ANR Selection Handlers ------------------
  const handleOpenAnrModal = () => {
    setOpenAnrModal(true);
  };

  const handleCloseAnrModal = () => {
    setOpenAnrModal(false);
  };

  const [partnerProject, setPartnerProject] = useState(null);
  const [currentProjectExtras, setCurrentProjectExtras] = useState(null);

  const handleSelectANR = async (selectedProject) => {
    // Update the selected item or new item based on the ANR
    if (selectedItem) {
      setSelectedItem({
        ...selectedItem,
        ANR: selectedProject.anr,
        auftragsbezeichnung: selectedProject.auftragsbezeichnung,
        projectId: selectedProject.projectId,
      });
    } else {
      setNewItem({
        ...newItem,
        ANR: selectedProject.anr,
        auftragsbezeichnung: selectedProject.auftragsbezeichnung,
        projectId: selectedProject.projectId,
      });
    }

    // Set validation state
    setAnrValid(true);
    setAnrMessage("ANR ausgewählt: " + selectedProject.anr);

    // Set current project extras
    setCurrentProjectExtras({
      menge: selectedProject.menge,
      mengeCost: selectedProject.mengeCost,
    });
    setPartnerProject(null); // Reset Lager project

    // Check for Lager project
    try {
      const response = await fetch(
        `${base_url}/v1/getPartnerProject/${selectedProject.projectId}`,
      );
      const result = await response.json();
      if (result.status === 1 && result.data) {
        console.log("Lager Project Found:", result.data);
        setPartnerProject(result.data);
        // You can add further logic here, e.g., notifying the user
        // toast.info(`Lager Project found: ${result.data.ANR}`);
      }
    } catch (error) {
      console.error("Error checking for Lager project:", error);
    }
  };

  // ------------------ Submit Handler (Add Item to Invoice) ------------------
  const handleSubmit = async () => {
    const itemToSave = { ...newItem, Abmessang: abmessungEntries };
    const maxSollValue = SelectedInvoiceAmount
      ? parseFloat(SelectedInvoiceAmount)
      : 0;
    const previousItemsCost = selectedItems.reduce(
      (sum, item) => sum + (item.totalStuckCost || 0),
      0,
    );
    // Check if calculated total exceeds Soll value
    if (
      toNumber(itemToSave.totalStuckCost) + previousItemsCost >
      maxSollValue
    ) {
      alert(
        `Gesamtkosten (${itemToSave.totalStuckCost}) können nicht höher sein als der Rechnungsbetrag (${maxSollValue})`,
      );
      return;
    }
    try {
      const response = await fetch(
        `${base_url}/v1/addItemsToInvoice/${selectedInvoiceId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items: [itemToSave] }),
        },
      );
      if (response.ok) {
        alert("Artikel erfolgreich hinzugefügt!");
        handleCloseDialog();
        // Possibly refresh data to see the new items
        fetchData();
      } else {
        alert("Fehler beim Hinzufügen des Artikels.");
      }
    } catch (error) {
      console.error("Error updating invoice:", error);
    }
  };

  const handleEditSubmit = async () => {
    const itemToSave = { ...selectedItem, Abmessang: abmessungEntries };
    const ANR = selectedItem.ANR;
    const maxSollValue = SelectedInvoiceAmount
      ? parseFloat(SelectedInvoiceAmount)
      : 0;
    // Check if calculated total exceeds Soll value
    if (itemToSave.totalStuckCost > maxSollValue) {
      alert(
        `Gesamtkosten (${itemToSave.totalStuckCost}) können nicht höher sein als der Rechnungsbetrag (${maxSollValue})`,
      );
      return;
    }

    try {
      const response = await fetch(
        `${base_url}/v1/updateInvoiceItem?invoiceId=${selectedInvoiceId}&ANR=${ANR}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(itemToSave),
        },
      );
      if (response.ok) {
        alert("Artikel erfolgreich bearbeitet!");
        handleEditCloseDialog();
        // Possibly refresh data to see the new items
        fetchData();
      } else {
        alert("Fehler beim Bearbeiten des Artikels.");
      }
    } catch (error) {
      console.error("Error updating invoice:", error);
    }
  };

  const deleteInvoiceItem = async (ANR) => {
    const confirmed = window.confirm(
      "Sind Sie sicher, dass Sie dieses Element wirklich löschen möchten?",
    );
    if (!confirmed) return;
    try {
      const response = await fetch(
        `${base_url}/v1/deleteInvoiceItem?invoiceId=${selectedInvoiceId}&ANR=${ANR}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
        },
      );

      if (response.ok) {
        alert("Element erfolgreich gelöscht");
        handleCloseViewDialog();
        fetchData(); // refresh list
      } else {
        alert("Fehler beim Löschen des Elements.");
      }
    } catch (error) {
      console.error("Error deleting invoice item:", error);
      alert("Ein unerwarteter Fehler ist aufgetreten.");
    }
  };
  // ------------------ Close Add Dialog ------------------
  const handleCloseDialog = () => {
    fetchMaterialSequenceNumber();
    setOpenDialog(false);
    // Reset newItem form
    setNewItem({
      position: "",
      singleStuckCost: "",
      numberOfStuck: "",
      totalStuckCost: "",
      ANR: "",
      percentage: 100,
      percentage2: "",
      description: "",
      totalTon: "",
      costPerTon: "",
      Abmessang: [],
      Wandstärke: "",
      Material1: "",
      Material2: "",
      Material3: "",
      Material4: "",
      bestullungNr: "",
      rechnuunglefrant: "",
      aufragNr: "",
      type: "Bestellung",
    });
    setAnrMessage("");
    setAbmessungEntries([]);
  };

  // When clicking the edit button on a row:
  const handleEditItem = (item) => {
    setSelectedItem(item);
    setOpenDialog(true); // open the dialog
    setOpenViewDialog(false);
    setAbmessungEntries(item.Abmessang);
  };

  // When closing the dialog (either Cancel or after saving), reset the editing state if needed:
  const handleEditCloseDialog = () => {
    setOpenDialog(false);
    setAbmessungEntries([]);
    setSelectedItem(null);
  };

  // ------------------ Close View Items Dialog ------------------
  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
  };

  // ------------------ Add Item button in the table ------------------
  const handleAddItemClick = (items, invoiceId, amount) => {
    setSelectedInvoiceId(invoiceId);
    setSelectedInvoiceAmount(amount);
    fetchMaterialSequenceNumber();
    setSelectedItems(items);
    setOpenDialog(true);
  };

  // ------------------ View Items ------------------
  const handleViewItems = (items, invoiceId, amount) => {
    setSelectedInvoiceId(invoiceId);
    setSelectedInvoiceAmount(amount);
    setSelectedItems(items);
    setOpenViewDialog(true);
  };

  // ------------------ Invoice Upload Logic ------------------
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const handleOpenUploadDialog = (invoiceId) => {
    setUploadInvoiceId(invoiceId);
    setOpenUploadDialog(true);
    setUploadProgress(0);
    setIsUploading(false);
  };

  const handleCloseUploadDialog = () => {
    setOpenUploadDialog(false);
    setFile(null);
    setUploadInvoiceId(null);
    setUploadProgress(0);
    setIsUploading(false);
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      // Check file size immediately on selection
      const maxSizeInMB = 500;
      const fileSizeInMB = selectedFile.size / (1024 * 1024);

      if (fileSizeInMB > maxSizeInMB) {
        alert(
          `Datei ist zu groß (${fileSizeInMB.toFixed(
            2,
          )}MB). Maximale Größe ist ${maxSizeInMB}MB.`,
        );
        e.target.value = ""; // Clear the input
        return;
      }

      setFile(selectedFile);
    }
  };
  const handleUploadInvoice = () => {
    if (!file || !uploadInvoiceId) return;

    // Validate file size before upload
    const maxSizeInMB = 500;
    const fileSizeInMB = file.size / (1024 * 1024);

    if (fileSizeInMB > maxSizeInMB) {
      alert(
        `Datei ist zu groß (${fileSizeInMB.toFixed(
          2,
        )}MB). Maximale Größe ist ${maxSizeInMB}MB.`,
      );
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append("invoices", file);

    // Use XMLHttpRequest for progress tracking
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) {
        const percentComplete = Math.round((event.loaded / event.total) * 100);
        setUploadProgress(percentComplete);
      }
    });

    xhr.addEventListener("load", async () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        alert("Datei erfolgreich hochgeladen!");
        await fetchData();
        handleCloseUploadDialog();
      } else {
        console.error(`Upload failed with status: ${xhr.status}`);
        alert(
          `Fehler beim Hochladen: ${xhr.statusText || "Unbekannter Fehler"}`,
        );
      }
      setIsUploading(false);
    });

    xhr.addEventListener("error", () => {
      console.error("Upload failed due to network error");
      alert("Netzwerkfehler beim Hochladen der Datei");
      setIsUploading(false);
    });

    xhr.addEventListener("abort", () => {
      console.log("Upload aborted");
      alert("Datei-Upload abgebrochen");
      setIsUploading(false);
    });

    // Open and send the request
    xhr.open(
      "POST",
      `${base_url}/v1/updateInvoiceDocument?id=${uploadInvoiceId}`,
      true,
    );
    xhr.timeout = 300000; // 5 minutes timeout
    xhr.send(formData);
  };

  // ------------------ View ALL Invoice Files Dialog ------------------
  const handleOpenInvoiceFilesDialog = (invoiceFiles) => {
    setSelectedInvoiceFiles(invoiceFiles);
    setOpenInvoiceFilesDialog(true);
  };
  const handleCloseInvoiceFilesDialog = () => {
    setSelectedInvoiceFiles([]);
    setOpenInvoiceFilesDialog(false);
  };

  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [deleteFileId, setDeleteFileId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const confirmDeleteFile = (fileId) => {
    setDeleteFileId(fileId);
    setShowDeleteConfirm(true);
  };

  const cancelDeleteFile = () => {
    setDeleteFileId(null);
    setShowDeleteConfirm(false);
  };

  const handleDeleteFile = async () => {
    if (!deleteFileId) return;

    try {
      setIsDeleteLoading(true);
      const response = await fetch(
        `${base_url}/v1/removeInvoiceDocument/${deleteFileId}`,
        {
          method: "POST",
        },
      );

      if (response.ok) {
        // Get the updated list of invoice files
        await fetchData();
        // If no more files, close the files dialog
        const currentInvoice = mergedData.find((inv) =>
          inv.invoiceFiles.some((file) => file._id === deleteFileId),
        );
        if (currentInvoice && currentInvoice.invoiceFiles.length <= 1) {
          handleCloseInvoiceFilesDialog();
        }
        // Show success message
        alert("Datei erfolgreich gelöscht!");
      } else {
        alert("Fehler beim Löschen der Datei.");
      }
    } catch (error) {
      console.error("Error deleting file:", error);
      alert("Fehler beim Löschen der Datei: " + error.message);
    } finally {
      setIsDeleteLoading(false);
      setShowDeleteConfirm(false);
      setDeleteFileId(null);
    }
  };

  // ------------------ QR Code Modal Handlers ------------------
  const handleOpenQrModal = (invoiceId) => {
    // Provide the link you'd like the phone to open
    // Make sure to use the invoice's ID, not _id
    setQrValue(`http://cnc-heidenau.de/UploadInvoicePageQR?id=${invoiceId}`);
    setOpenQrModal(true);
  };
  const handleCloseQrModal = () => {
    setOpenQrModal(false);
    setQrValue("");
  };

  // ------------------ Exports: PDF, Excel (Rechnungen), Excel (ANR) ------------------
  const exportToPDF = () => {
    const doc = new jsPDF("landscape");
    const tableColumn = [
      "Rechnungsnummer Intern",
      "Konto",
      "Datum",
      "Gkto",
      "Kunde von Agenda",
      "Kunde von Abdul",
      "Beleg1",
      "Soll",
      "Rechnung 1.19",
    ];
    const tableRows = mergedData.map((row) => [
      formatInvoiceNumber(row.id, row.Datum),
      row.Konto,
      formatDate(row.Datum),
      row.Gkto,
      row.customerByAgenda,
      row.customerByAbdul,
      row.Beleg1,
      row.Soll,
      row.sollPercent,
    ]);

    const totalSoll = mergedData
      .reduce((sum, row) => {
        const sollValue = parseFloat(row.Soll) || 0;
        return sum + sollValue;
      }, 0)
      .toFixed(2);

    const totalRow = ["Total", "", "", "", "", "", "", totalSoll, ""];
    tableRows.push(totalRow);

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
    });

    doc.save("rechnungs.pdf");
  };

  const exportToExcel = () => {
    const excelData = mergedData.map((row) => ({
      "Rechnungsnummer Intern": formatInvoiceNumber(row.id, row.Datum),
      "Hat Artikel": row.items.length > 0 ? "Ja" : "Nein",
      Konto: row.Konto,
      Datum: formatDate(row.Datum),
      Gkto: row.Gkto,
      "Kunde von Agenda": row.customerByAgenda,
      "Kunde von Abdul": row.customerByAbdul,
      Beleg1: row.Beleg1,
      Soll: row.Soll,
      "Rechnung 1.19": row.sollPercent,
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Rechnungen");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    const url = window.URL.createObjectURL(data);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "rechnungs.xlsx");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportANRToExcel = () => {
    const workbook = XLSX.utils.book_new();
    const anrData = organizeDataByANR();

    const summaryData = anrData.map((group) => ({
      ANR: group.anr,
      "Total Cost (€)": group.totalCost.toFixed(2),
      "Total Quantity": group.totalQuantity,
      "Invoice Count": group.invoiceCount,
      Customers: group.uniqueCustomers.join(", "),
      "Related Invoice Numbers": [
        ...new Set(group.items.map((item) => item.invoiceId)),
      ].join(", "),
      "Related Beleg Numbers": [
        ...new Set(group.items.map((item) => item.beleg)),
      ].join(", "),
    }));

    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(summaryData),
      "ANR Summary",
    );

    const detailedData = [];
    anrData.forEach((group) => {
      // Group-level row
      detailedData.push({
        ANR: group.anr,
        "Total Cost": group.totalCost.toFixed(2) + " €",
        "Total Quantity": group.totalQuantity,
        "Invoice Numbers": [
          ...new Set(group.items.map((item) => item.invoiceId)),
        ].join(", "),
        "Beleg Numbers": [
          ...new Set(group.items.map((item) => item.beleg)),
        ].join(", "),
      });
      detailedData.push({});
      // Each item row
      group.items.forEach((item) => {
        detailedData.push({
          ANR: group.anr,
          "Invoice #": item.invoiceId,
          Beleg: item.beleg,
          Date: formatDate(item.date),
          Customer: item.customer,
          "Material Type": item.Material2,
          Description: item.description,
          Quantity: item.numberOfStuck,
          "Unit Price (€)": parseFloat(item.singleStuckCost).toFixed(2),
          "Total Cost (€)": parseFloat(item.totalStuckCost).toFixed(2),
        });
      });
      detailedData.push({});
      detailedData.push({});
    });

    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(detailedData),
      "ANR Details",
    );

    XLSX.writeFile(workbook, "anr-report.xlsx");
  };

  const organizeDataByANR = () => {
    const anrGroups = new Map();
    mergedData.forEach((invoice) => {
      if (invoice.items && invoice.items.length > 0) {
        invoice.items.forEach((item) => {
          if (item.ANR) {
            if (!anrGroups.has(item.ANR)) {
              anrGroups.set(item.ANR, {
                items: [],
                totalCost: 0,
                totalQuantity: 0,
                customers: new Set(),
                invoices: new Set(),
                belegs: new Set(),
              });
            }
            const group = anrGroups.get(item.ANR);
            group.items.push({
              ...item,
              invoiceId: invoice.id,
              beleg: invoice.Beleg1,
              customer: invoice.Buchungstext,
              date: invoice.Datum,
            });
            group.totalCost += parseFloat(item.totalStuckCost) || 0;
            group.totalQuantity += parseFloat(item.numberOfStuck) || 0;
            group.customers.add(invoice.Buchungstext);
            group.invoices.add(invoice.id);
            group.belegs.add(invoice.Beleg1);
          }
        });
      }
    });

    return Array.from(anrGroups.entries()).map(([anr, data]) => ({
      anr,
      items: data.items,
      totalCost: data.totalCost,
      totalQuantity: data.totalQuantity,
      uniqueCustomers: Array.from(data.customers),
      invoiceCount: data.invoices.size,
      uniqueBelegs: Array.from(data.belegs),
    }));
  };

  // ------------------ Memoized Filtered + Sorted Data ------------------
  const filteredData = useMemo(() => {
    let data = mergedData;
    if (columnFilters.id) {
      data = data.filter((item) =>
        item.id
          .toString()
          .toLowerCase()
          .includes(columnFilters.id.toLowerCase()),
      );
    }
    if (columnFilters.status) {
      data = data.filter((item) => {
        const statusText =
          item.items.length > 0 ? "hat Artikel" : "Keine Artikel";
        return statusText
          .toLowerCase()
          .includes(columnFilters.status.toLowerCase());
      });
    }
    if (columnFilters.Konto) {
      data = data.filter(
        (item) =>
          item.Konto &&
          item.Konto.toString()
            .toLowerCase()
            .includes(columnFilters.Konto.toLowerCase()),
      );
    }
    if (columnFilters.Datum) {
      data = data.filter((item) =>
        formatDate(item.Datum)
          .toLowerCase()
          .includes(columnFilters.Datum.toLowerCase()),
      );
    }
    if (columnFilters.customerByAbdul) {
      data = data.filter(
        (item) =>
          item.customerByAbdul &&
          item.customerByAbdul
            .toLowerCase()
            .includes(columnFilters.customerByAbdul.toLowerCase()),
      );
    }
    if (columnFilters.Beleg1) {
      data = data.filter(
        (item) =>
          item.Beleg1 &&
          item.Beleg1.toLowerCase().includes(
            columnFilters.Beleg1.toLowerCase(),
          ),
      );
    }
    if (columnFilters.Soll) {
      data = data.filter(
        (item) =>
          item.Soll &&
          item.Soll.toString()
            .toLowerCase()
            .includes(columnFilters.Soll.toLowerCase()),
      );
    }
    if (columnFilters.anr) {
      data = data.filter((item) =>
        item.items.some((article) => {
          return article.ANR?.toLowerCase().includes(
            columnFilters.anr.toLowerCase(),
          );
        }),
      );
    }
    // Sort logic
    if (sortConfig.key) {
      data = data.slice().sort((a, b) => {
        let aVal = getSortValue(a, sortConfig.key);
        let bVal = getSortValue(b, sortConfig.key);
        if (sortConfig.key.toLowerCase() === "datum") {
          aVal = parseDate(aVal);
          bVal = parseDate(bVal);
        } else if (["id", "soll"].includes(sortConfig.key.toLowerCase())) {
          aVal = parseFloat(aVal);
          bVal = parseFloat(bVal);
        } else {
          aVal = aVal.toString().toLowerCase();
          bVal = bVal.toString().toLowerCase();
        }
        if (aVal < bVal) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return data;
  }, [mergedData, columnFilters, sortConfig]);

  // For displaying material sequence "NN-25" if 2025
  const currentYearLastTwoDigits = new Date()
    .getFullYear()
    .toString()
    .slice(-2);

  // ------------------ RENDER ------------------
  return (
    <div className="min-h-screen bg-gray-50 p-8">
      {/* Header Section */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          Rechnungsverwaltung
        </h1>
        <p className="mt-2 text-gray-600">
          Verwalten und verfolgen Sie Ihre Rechnungen effizient
        </p>
      </div>

      {/* Action Buttons */}
      <div className="mb-6 flex flex-wrap items-center gap-4">
        <button
          onClick={exportToPDF}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 transition-colors"
        >
          <ArrowDownToLine className="h-5 w-5" />
          Exportieren als PDF
        </button>

        <button
          onClick={exportToExcel}
          className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-white hover:bg-green-700 transition-colors"
        >
          <FileSpreadsheet className="h-5 w-5" />
          Exportieren als Excel
        </button>

        <button
          onClick={exportANRToExcel}
          className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-white hover:bg-green-700 transition-colors"
        >
          <FileSpreadsheet className="h-5 w-5" />
          ANR-Bericht herunterladen
        </button>

        <button
          onClick={handleOpenAddRechnungModal}
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700 transition-colors"
        >
          <Plus className="h-5 w-5" />
          Rechnung hinzufügen
        </button>
      </div>

      <div>
        <input
          type="text"
          placeholder="ANR suchen"
          onChange={(e) =>
            setColumnFilters((prev) => ({
              ...prev,
              anr: e.target.value,
            }))
          }
          className="w-full text-sm text-gray-900 border rounded p-2 mb-2"
        />
      </div>

      {/* Main Table */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th
                    className="px-6 py-3 text-left text-sm font-semibold text-gray-900 cursor-pointer"
                    onClick={() => requestSort("id")}
                  >
                    Rechnungs #
                    {sortConfig.key === "id" &&
                      sortConfig.direction === "asc" && (
                        <ChevronUp size={16} className="inline-block ml-1" />
                      )}
                    {sortConfig.key === "id" &&
                      sortConfig.direction === "desc" && (
                        <ChevronDown size={16} className="inline-block ml-1" />
                      )}
                  </th>
                  <th
                    className="px-6 py-3 text-left text-sm font-semibold text-gray-900 cursor-pointer"
                    onClick={() => requestSort("status")}
                  >
                    Status
                    {sortConfig.key === "status" &&
                      sortConfig.direction === "asc" && (
                        <ChevronUp size={16} className="inline-block ml-1" />
                      )}
                    {sortConfig.key === "status" &&
                      sortConfig.direction === "desc" && (
                        <ChevronDown size={16} className="inline-block ml-1" />
                      )}
                  </th>
                  <th
                    className="px-6 py-3 text-left text-sm font-semibold text-gray-900 cursor-pointer"
                    onClick={() => requestSort("Konto")}
                  >
                    Konto
                    {sortConfig.key === "Konto" &&
                      sortConfig.direction === "asc" && (
                        <ChevronUp size={16} className="inline-block ml-1" />
                      )}
                    {sortConfig.key === "Konto" &&
                      sortConfig.direction === "desc" && (
                        <ChevronDown size={16} className="inline-block ml-1" />
                      )}
                  </th>
                  <th
                    className="px-6 py-3 text-left text-sm font-semibold text-gray-900 cursor-pointer"
                    onClick={() => requestSort("Datum")}
                  >
                    Datum
                    {sortConfig.key === "Datum" &&
                      sortConfig.direction === "asc" && (
                        <ChevronUp size={16} className="inline-block ml-1" />
                      )}
                    {sortConfig.key === "Datum" &&
                      sortConfig.direction === "desc" && (
                        <ChevronDown size={16} className="inline-block ml-1" />
                      )}
                  </th>
                  <th
                    className="px-6 py-3 text-left text-sm font-semibold text-gray-900 cursor-pointer"
                    onClick={() => requestSort("customerByAbdul")}
                  >
                    Kunde
                    {sortConfig.key === "customerByAbdul" &&
                      sortConfig.direction === "asc" && (
                        <ChevronUp size={16} className="inline-block ml-1" />
                      )}
                    {sortConfig.key === "customerByAbdul" &&
                      sortConfig.direction === "desc" && (
                        <ChevronDown size={16} className="inline-block ml-1" />
                      )}
                  </th>
                  <th
                    className="px-6 py-3 text-left text-sm font-semibold text-gray-900 cursor-pointer"
                    onClick={() => requestSort("Beleg1")}
                  >
                    Beleg
                    {sortConfig.key === "Beleg1" &&
                      sortConfig.direction === "asc" && (
                        <ChevronUp size={16} className="inline-block ml-1" />
                      )}
                    {sortConfig.key === "Beleg1" &&
                      sortConfig.direction === "desc" && (
                        <ChevronDown size={16} className="inline-block ml-1" />
                      )}
                  </th>
                  <th
                    className="px-6 py-3 text-left text-sm font-semibold text-gray-900 cursor-pointer"
                    onClick={() => requestSort("Soll")}
                  >
                    Betrag
                    {sortConfig.key === "Soll" &&
                      sortConfig.direction === "asc" && (
                        <ChevronUp size={16} className="inline-block ml-1" />
                      )}
                    {sortConfig.key === "Soll" &&
                      sortConfig.direction === "desc" && (
                        <ChevronDown size={16} className="inline-block ml-1" />
                      )}
                  </th>
                  <th
                    className="px-6 py-3 text-left text-sm font-semibold text-gray-900 cursor-pointer"
                    onClick={() => requestSort("skontoPercentage")}
                  >
                    SKONTO
                    {sortConfig.key === "skontoPercentage" &&
                      sortConfig.direction === "asc" && (
                        <ChevronUp size={16} className="inline-block ml-1" />
                      )}
                    {sortConfig.key === "skontoPercentage" &&
                      sortConfig.direction === "desc" && (
                        <ChevronDown size={16} className="inline-block ml-1" />
                      )}
                  </th>
                  <th className="px-6 py-3 text-sm font-semibold text-gray-900">
                    Aktionen
                  </th>
                </tr>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-6 py-1">
                    <input
                      type="text"
                      placeholder="Search"
                      value={columnFilters.id}
                      onChange={(e) =>
                        setColumnFilters((prev) => ({
                          ...prev,
                          id: e.target.value,
                        }))
                      }
                      className="w-full text-sm text-gray-900 border rounded"
                    />
                  </th>
                  <th className="px-6 py-1">
                    <input
                      type="text"
                      placeholder="Search"
                      value={columnFilters.status}
                      onChange={(e) =>
                        setColumnFilters((prev) => ({
                          ...prev,
                          status: e.target.value,
                        }))
                      }
                      className="w-full text-sm text-gray-900 border rounded"
                    />
                  </th>
                  <th className="px-6 py-1">
                    <input
                      type="text"
                      placeholder="Search"
                      value={columnFilters.Konto}
                      onChange={(e) =>
                        setColumnFilters((prev) => ({
                          ...prev,
                          Konto: e.target.value,
                        }))
                      }
                      className="w-full text-sm text-gray-900 border rounded"
                    />
                  </th>
                  <th className="px-6 py-1">
                    <input
                      type="text"
                      placeholder="Search"
                      value={columnFilters.Datum}
                      onChange={(e) =>
                        setColumnFilters((prev) => ({
                          ...prev,
                          Datum: e.target.value,
                        }))
                      }
                      className="w-full text-sm text-gray-900 border rounded"
                    />
                  </th>
                  <th className="px-6 py-1">
                    <input
                      type="text"
                      placeholder="Search"
                      value={columnFilters.customerByAbdul}
                      onChange={(e) =>
                        setColumnFilters((prev) => ({
                          ...prev,
                          customerByAbdul: e.target.value,
                        }))
                      }
                      className="w-full text-sm text-gray-900 border rounded"
                    />
                  </th>
                  <th className="px-6 py-1">
                    <input
                      type="text"
                      placeholder="Search"
                      value={columnFilters.Beleg1}
                      onChange={(e) =>
                        setColumnFilters((prev) => ({
                          ...prev,
                          Beleg1: e.target.value,
                        }))
                      }
                      className="w-full text-sm text-gray-900 border rounded"
                    />
                  </th>
                  <th className="px-6 py-1">
                    <input
                      type="text"
                      placeholder="Search"
                      value={columnFilters.Soll}
                      onChange={(e) =>
                        setColumnFilters((prev) => ({
                          ...prev,
                          Soll: e.target.value,
                        }))
                      }
                      className="w-full text-sm text-gray-900 border rounded"
                    />
                  </th>
                  <th className="px-6 py-1">
                    <input
                      type="text"
                      placeholder="Search"
                      value={columnFilters.skontoPercentage || ""}
                      onChange={(e) =>
                        setColumnFilters((prev) => ({
                          ...prev,
                          skontoPercentage: e.target.value,
                        }))
                      }
                      className="w-full text-sm text-gray-900 border rounded"
                    />
                  </th>
                  <th className="px-6 py-1"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredData.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {formatInvoiceNumber(row.id, row.Datum)}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${
                          row.items.length > 0
                            ? "bg-green-100 text-green-700"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        <span
                          className={`h-1.5 w-1.5 rounded-full ${
                            row.items.length > 0
                              ? "bg-green-600"
                              : "bg-gray-600"
                          }`}
                        />
                        {row.items.length > 0 ? "hat Artikel" : "Keine Artikel"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {row.Konto}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {formatDate(row.Datum)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {row.customerByAbdul}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {row.Beleg1}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {row.Soll}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {row.skontoPercentage ? (
                        <div>
                          <div className="text-sm font-medium">
                            {parseFloat(row.skontoPercentage)}%
                          </div>
                          <div className="text-xs text-gray-500">
                            €
                            {(
                              (parseFloat(row.Soll || 0) *
                                parseFloat(row.skontoPercentage)) /
                              100
                            ).toFixed(2)}
                          </div>
                        </div>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2 justify-between">
                        <div className="flex gap-2">
                          {/* Add Item Button */}
                          <button
                            onClick={() =>
                              handleAddItemClick(row.items, row.id, row.Soll)
                            }
                            className="inline-flex items-center gap-1 rounded-lg bg-blue-50 px-3 py-1 text-sm font-medium text-blue-600 hover:bg-blue-100"
                          >
                            <Plus className="h-4 w-4" />
                            Hinzufügen
                          </button>

                          {/* If there are items, show View button */}
                          {row.items.length > 0 && (
                            <button
                              onClick={() =>
                                handleViewItems(row.items, row.id, row.Soll)
                              }
                              className="inline-flex items-center gap-1 rounded-lg bg-gray-50 px-3 py-1 text-sm font-medium text-gray-600 hover:bg-gray-100"
                            >
                              Anzeigen
                            </button>
                          )}

                          {/* Always show both "View Invoices" (if files exist) and "Upload" buttons */}
                          {row.invoiceFiles && row.invoiceFiles.length > 0 && (
                            <button
                              onClick={() =>
                                handleOpenInvoiceFilesDialog(row.invoiceFiles)
                              }
                              className="inline-flex items-center gap-1 rounded-lg bg-green-50 px-3 py-1 text-sm font-medium text-green-600 hover:bg-green-100"
                            >
                              Rechnungen Anzeigen
                            </button>
                          )}
                          <button
                            onClick={() => handleOpenUploadDialog(row.id)}
                            className="inline-flex items-center gap-1 rounded-lg bg-indigo-50 px-3 py-1 text-sm font-medium text-indigo-600 hover:bg-indigo-100"
                          >
                            Hochladen
                          </button>

                          {/* QR button */}
                          <button
                            onClick={() => handleOpenQrModal(row.id)}
                            className="inline-flex items-center gap-1 rounded-lg bg-yellow-50 px-3 py-1 text-sm font-medium text-yellow-600 hover:bg-yellow-100"
                          >
                            QR
                          </button>
                        </div>

                        {/* Delete button - always at the right end */}
                        <button
                          onClick={() => handleDeleteRechnungClick(row.id)}
                          className="inline-flex items-center gap-1 rounded-lg bg-red-50 px-3 py-1 text-sm font-medium text-red-600 hover:bg-red-100"
                        >
                          <Trash2 className="h-4 w-4" />
                          Löschen
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ------------------------------------------
          Add Item Dialog
      ------------------------------------------- */}
      {openDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900">
                  {selectedItem
                    ? "Edit Material Entry"
                    : "Add New Material Entry"}{" "}
                  {materialSequenceNumber &&
                    `${materialSequenceNumber}-${currentYearLastTwoDigits}`}
                </h2>
                <button
                  onClick={
                    selectedItem ? handleEditCloseDialog : handleCloseDialog
                  }
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bestellnummer
                  </label>
                  <input
                    type="text"
                    value={selectedItem?.bestullungNr ?? newItem.bestullungNr}
                    onChange={(e) => {
                      const value = e.target.value;
                      selectedItem
                        ? setSelectedItem({
                            ...selectedItem,
                            bestullungNr: value,
                          })
                        : setNewItem({ ...newItem, bestullungNr: value });
                    }}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ANR
                  </label>
                  <div className="flex">
                    <input
                      type="text"
                      value={selectedItem?.ANR ?? newItem.ANR}
                      readOnly
                      className={`w-full rounded-l-lg border px-4 py-2 focus:ring-1 outline-none ${
                        anrValid === true
                          ? "border-green-500 focus:border-green-500 focus:ring-green-500"
                          : anrValid === false
                            ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                            : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                      }`}
                    />
                    <button
                      type="button"
                      onClick={handleOpenAnrModal}
                      className="bg-blue-600 hover:bg-blue-700 text-white rounded-r-lg px-3 flex items-center"
                    >
                      <Search className="h-4 w-4" />
                    </button>
                  </div>
                  {anrMessage && (
                    <p
                      className={`mt-1 text-sm ${
                        anrValid ? "text-green-600" : "text-red-600"
                      }`}
                    >
                      {anrMessage}
                    </p>
                  )}
                </div>
              </div>

              {/* Costs Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Cost Information
                </h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Stückpreis
                    </label>
                    <input
                      type="number"
                      value={
                        selectedItem?.singleStuckCost ?? newItem.singleStuckCost
                      }
                      onChange={handleSingleStuckCostChange}
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Menge
                    </label>
                    <input
                      type="number"
                      value={
                        selectedItem?.numberOfStuck ?? newItem.numberOfStuck
                      }
                      onChange={handleNumberOfStuckChange}
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Gesamtkosten
                    </label>
                    <input
                      type="number"
                      value={
                        selectedItem?.totalStuckCost ?? newItem.totalStuckCost
                      }
                      onChange={handleTotalStuckCostChange}
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Materials Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Material Information
                </h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Materialart
                    </label>
                    <select
                      value={selectedItem?.Material2 ?? newItem.Material2}
                      onChange={(e) => handleMaterialArtChange(e, "Material2")}
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                    >
                      <option value="">Materialart wählen</option>
                      {materialArtOptions.map((option) => (
                        <option key={option.name} value={option.name}>
                          {option.name}
                        </option>
                      ))}
                      <option value="add-new">
                        Neue Materialart hinzufügen...
                      </option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Kostenart
                    </label>
                    <select
                      value={selectedItem?.Material1 ?? newItem.Material1}
                      onChange={handleKostenArtChange}
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                    >
                      <option value="">Kostenart wählen</option>
                      {kostenArtOptions.map((option) => (
                        <option key={option.name} value={option.name}>
                          {option.name}
                        </option>
                      ))}
                      <option value="add-new">
                        Neue Kostenart hinzufügen...
                      </option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Dimensions Section */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-900">
                    Abmessungen
                  </h3>
                  <button
                    onClick={handleAddAbmessung}
                    className="inline-flex items-center gap-1 rounded-lg bg-blue-50 px-3 py-1 text-sm font-medium text-blue-600 hover:bg-blue-100"
                  >
                    <Plus className="h-4 w-4" />
                    Add Dimension
                  </button>
                </div>
                <div className="space-y-3">
                  {abmessungEntries.map((entry, index) => (
                    <div key={index} className="flex gap-3 items-start">
                      <select
                        value={entry.name}
                        onChange={(e) =>
                          handleAbmessungChange(index, "name", e.target.value)
                        }
                        className="rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                      >
                        <option value="">Select Type</option>
                        {abmessungNames.map((name) => (
                          <option key={name} value={name}>
                            {name}
                          </option>
                        ))}
                      </select>
                      <select
                        value={entry.unit}
                        onChange={(e) =>
                          handleAbmessungChange(index, "unit", e.target.value)
                        }
                        className="rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                      >
                        <option value="">Select Unit</option>
                        {abmessungUnits.map((unit) => (
                          <option key={unit} value={unit}>
                            {unit}
                          </option>
                        ))}
                      </select>
                      <input
                        type="number"
                        value={entry.value}
                        onChange={(e) =>
                          handleAbmessungChange(index, "value", e.target.value)
                        }
                        className="flex-1 rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                        placeholder="Value"
                      />
                      <button
                        onClick={() => handleRemoveAbmessung(index)}
                        className="text-gray-400 hover:text-gray-500"
                      >
                        <X className="h-6 w-6" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Material Distribution */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Material Distribution
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prozentsatz für ANR
                    </label>
                    <input
                      type="number"
                      value={selectedItem?.percentage ?? newItem.percentage}
                      onChange={handlePercentageChange}
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                      min="0"
                      max="100"
                    />
                    {((selectedItem?.percentage ?? newItem.percentage) < 0 ||
                      (selectedItem?.percentage ?? newItem.percentage) >
                        100) && (
                      <p className="mt-1 text-sm text-red-600">
                        Please enter a value between 0 and 100
                      </p>
                    )}
                  </div>

                  <div className="relative py-3">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300"></div>
                    </div>
                    <div className="relative flex justify-center">
                      <span className="bg-white px-4 text-sm text-gray-500">
                        {partnerProject
                          ? `Verbleibender Prozentsatz (Lager: ${partnerProject.ANR})`
                          : "Verbleibender Prozentsatz"}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {partnerProject
                        ? `Prozentsatz für Partnerprojekt`
                        : "Prozentsatz für Lagerbestand"}
                    </label>
                    <input
                      type="number"
                      value={selectedItem?.percentage2 ?? newItem.percentage2}
                      onChange={handlePercentage2Change}
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                      min="0"
                      max="100"
                    />
                    {((selectedItem?.percentage2 ?? newItem.percentage2) < 0 ||
                      (selectedItem?.percentage2 ?? newItem.percentage2) >
                        100) && (
                      <p className="mt-1 text-sm text-red-600">
                        Please enter a value between 0 and 100
                      </p>
                    )}
                  </div>
                </div>
                {/* Display Project and Lager Info */}
                {(currentProjectExtras || partnerProject) && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">
                      Projekt Informationen
                    </h4>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      {currentProjectExtras && (
                        <div>
                          <p className="text-xs text-gray-500 font-medium">
                            Aktuelles Projekt (ANR:{" "}
                            {selectedItem?.ANR ?? newItem.ANR})
                          </p>
                          <div className="flex gap-4 mt-1">
                            <div>
                              <span className="text-xs text-gray-400 block">
                                Menge
                              </span>
                              <span className="text-sm font-medium text-gray-900">
                                {currentProjectExtras.menge}
                              </span>
                            </div>
                            <div>
                              <span className="text-xs text-gray-400 block">
                                Menge Preis
                              </span>
                              <span className="text-sm font-medium text-gray-900">
                                {currentProjectExtras.mengeCost} €
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                      {partnerProject && (
                        <div>
                          <p className="text-xs text-blue-600 font-medium">
                            Lager Projekt (ANR: {partnerProject.ANR})
                          </p>
                          <div className="flex gap-4 mt-1">
                            <div>
                              <span className="text-xs text-gray-400 block">
                                Menge
                              </span>
                              <span className="text-sm font-medium text-gray-900">
                                {partnerProject.Menge}
                              </span>
                            </div>
                            <div>
                              <span className="text-xs text-gray-400 block">
                                Menge Preis
                              </span>
                              <span className="text-sm font-medium text-gray-900">
                                {partnerProject.MengeCost} €
                              </span>
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 mt-2 italic">
                            Bitte setzen Sie den verbleibenden Prozentsatz für
                            dieses Partnerprojekt.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={
                  selectedItem ? handleEditCloseDialog : handleCloseDialog
                }
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800"
              >
                Abbrechen
              </button>
              <button
                onClick={selectedItem ? handleEditSubmit : handleSubmit}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Speichern
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------
          View Items Dialog
      ------------------------------------------- */}
      {openViewDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900">
                  Artikel anzeigen
                </h2>
                <button
                  onClick={handleCloseViewDialog}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {selectedItems.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {selectedItems.map((item, index) => (
                    <div
                      key={index}
                      className="bg-gray-50 rounded-lg p-6 border border-gray-200 hover:shadow-md transition-shadow"
                    >
                      <div className="space-y-4">
                        <div className="flex justify-between items-start">
                          <h3 className="text-lg font-medium text-gray-900">
                            Position: {item.position}
                          </h3>
                          <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-sm font-medium text-blue-800">
                            {item.type}
                          </span>
                        </div>

                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-gray-500">Bestellnummer</p>
                            <p className="font-medium">{item.bestullungNr}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">ANR</p>
                            <p className="font-medium">{item.ANR}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Single Cost</p>
                            <p className="font-medium">
                              {item.singleStuckCost} €
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500">Gesamtkosten</p>
                            <p className="font-medium">
                              {item.totalStuckCost} €
                            </p>
                          </div>
                        </div>

                        {item.description && (
                          <div>
                            <p className="text-gray-500">Beschreibung</p>
                            <p className="mt-1 text-sm text-gray-900">
                              {item.description}
                            </p>
                          </div>
                        )}

                        {item.Abmessang && item.Abmessang.length > 0 && (
                          <div>
                            <p className="text-gray-500 mb-2">Abmessungen</p>
                            <div className="grid grid-cols-2 gap-2">
                              {item.Abmessang.map((abm, idx) => (
                                <div key={idx} className="text-sm">
                                  <span className="font-medium">
                                    {abm.name}:{" "}
                                  </span>
                                  {abm.value} {abm.unit}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex justify-between items-start mt-8">
                        <button
                          className="inline-flex items-center gap-1 rounded-lg bg-red-50 px-3 py-1 text-sm font-medium text-red-600 hover:bg-red-100"
                          onClick={() => deleteInvoiceItem(item.ANR)}
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </button>
                        <button
                          className="inline-flex items-center gap-1 rounded-lg bg-blue-50 px-3 py-1 text-sm font-medium text-blue-600 hover:bg-blue-100"
                          onClick={() => handleEditItem(item)}
                        >
                          <Pencil className="h-4 w-4" />
                          Edit
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500">Keine Artikel vorhanden</p>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200">
              <button
                onClick={handleCloseViewDialog}
                className="ml-auto block px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------
          View Invoice Files Dialog
      ------------------------------------------- */}
      {openInvoiceFilesDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                Hochgeladene Rechnungen
              </h2>
              <button
                onClick={handleCloseInvoiceFilesDialog}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {selectedInvoiceFiles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {selectedInvoiceFiles.map((file, idx) => (
                  <div
                    key={file._id || idx}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex justify-between items-center">
                      <p className="text-sm text-gray-600 mb-2">
                        File: {file.fileName || `Invoice File ${idx + 1}`}
                      </p>
                      <button
                        onClick={() => confirmDeleteFile(file._id)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>

                    {/* If PDF */}
                    {file.mediaType && file.mediaType.includes("pdf") ? (
                      <div className="h-64 overflow-auto border">
                        <embed
                          src={file.url}
                          type="application/pdf"
                          width="100%"
                          height="100%"
                        />
                      </div>
                    ) : file.mediaType &&
                      file.mediaType.startsWith("image/") ? (
                      /* If it's an image (jpeg, png, etc.) */
                      <img
                        src={file.url}
                        alt="Invoice"
                        className="max-h-64 object-contain mx-auto cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => openImagePreview(file.url)}
                      />
                    ) : (
                      /* Otherwise just show a link */
                      <a
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline text-sm"
                      >
                        Open File
                      </a>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500">
                  Keine Rechnungsdateien verfügbar.
                </p>
              </div>
            )}

            <div className="mt-6 text-right">
              <button
                onClick={handleCloseInvoiceFilesDialog}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------
          Upload Invoice Dialog
      ------------------------------------------- */}
      {openUploadDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                Rechnung Hochladen
              </h2>
              <button
                onClick={handleCloseUploadDialog}
                disabled={isUploading}
                className={`text-gray-400 hover:text-gray-500 ${
                  isUploading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-gray-700">
                Wählen Sie eine PDF- oder Bilddatei zum Hochladen aus.
                <span className="block text-xs text-gray-500 mt-1">
                  (Maximale Dateigröße: 500 MB)
                </span>
              </p>

              <input
                type="file"
                onChange={handleFileChange}
                disabled={isUploading}
                className={isUploading ? "opacity-50 cursor-not-allowed" : ""}
              />

              {isUploading && (
                <div className="mt-4">
                  <div className="flex justify-between text-sm font-medium text-gray-700 mb-1">
                    <span>Upload fortschritt</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div
                      className="bg-blue-600 h-2.5 rounded-full"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Bitte schließen Sie dieses Fenster nicht während des
                    Uploads.
                  </p>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={handleCloseUploadDialog}
                disabled={isUploading}
                className={`px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800 ${
                  isUploading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                Abbrechen
              </button>
              <button
                onClick={handleUploadInvoice}
                disabled={!file || isUploading}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg ${
                  !file || isUploading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-indigo-600 hover:bg-indigo-700"
                }`}
              >
                {isUploading ? "Hochladen..." : "Hochladen"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------
          QR Code Modal
      ------------------------------------------- */}
      {openQrModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">QR-Code scannen</h2>
              <button
                onClick={handleCloseQrModal}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex justify-center">
              <QRCodeCanvas
                value={qrValue}
                size={256}
                bgColor="#FFFFFF"
                fgColor="#000000"
                level="L"
              />
            </div>
            <p className="text-center mt-4">
              Scannen Sie diesen Code mit Ihrem Smartphone
            </p>
          </div>
        </div>
      )}

      {/* ------------------------------------------
          Image Preview Modal
      ------------------------------------------- */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4"
          onClick={closeImagePreview}
        >
          <div className="relative max-w-4xl w-full h-full flex items-center justify-center">
            <img
              src={selectedImage}
              alt="Invoice Preview"
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={closeImagePreview}
              className="absolute top-4 right-4 text-white hover:text-gray-200"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>
      )}

      {/* ------------------------------------------
          ANR Selection Modal
      ------------------------------------------- */}
      <ANRModal
        open={openAnrModal}
        onClose={handleCloseAnrModal}
        onSelect={handleSelectANR}
        isDarkMode={false}
      />

      {/* ------------------------------------------
          Delete Confirmation Dialog
      ------------------------------------------- */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Datei löschen
            </h3>
            <p className="text-gray-600 mb-6">
              Sind Sie sicher, dass Sie diese Datei löschen möchten? Diese
              Aktion kann nicht rückgängig gemacht werden.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={cancelDeleteFile}
                disabled={isDeleteLoading}
                className={`px-4 py-2 rounded-md text-gray-700 border border-gray-300 hover:bg-gray-50 ${
                  isDeleteLoading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                Abbrechen
              </button>
              <button
                onClick={handleDeleteFile}
                disabled={isDeleteLoading}
                className={`px-4 py-2 rounded-md text-white ${
                  isDeleteLoading
                    ? "bg-red-400 cursor-not-allowed"
                    : "bg-red-600 hover:bg-red-700"
                }`}
              >
                {isDeleteLoading ? (
                  <span className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Löschen...
                  </span>
                ) : (
                  "Löschen"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------
          Delete Rechnung Confirmation Modal
      ------------------------------------------- */}
      {showDeleteConfirmRechnung && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Rechnung löschen
            </h3>
            <p className="text-gray-600 mb-6">
              Sind Sie sicher, dass Sie diese Rechnung löschen möchten? Diese
              Aktion kann nicht rückgängig gemacht werden und alle zugehörigen
              Daten werden permanent entfernt.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleCancelDeleteRechnung}
                disabled={isDeleteLoading}
                className={`px-4 py-2 rounded-md text-gray-700 border border-gray-300 hover:bg-gray-50 ${
                  isDeleteLoading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                Abbrechen
              </button>
              <button
                onClick={handleConfirmDeleteRechnung}
                disabled={isDeleteLoading}
                className={`px-4 py-2 rounded-md text-white ${
                  isDeleteLoading
                    ? "bg-red-400 cursor-not-allowed"
                    : "bg-red-600 hover:bg-red-700"
                }`}
              >
                {isDeleteLoading ? (
                  <span className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Löschen...
                  </span>
                ) : (
                  "Löschen"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------
          Add Rechnung Modal
      ------------------------------------------- */}
      {openAddRechnungModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900">
                  Neue Rechnung hinzufügen
                </h2>
                <button
                  onClick={handleCloseAddRechnungModal}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Add Rechnung Form */}
              <form className="space-y-6">
                {/* Invoice Type Selection */}
                <div className="mb-6">
                  {/* Unified Search for both Kunde and Lieferant */}
                  <div className="mb-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Kunde/Lieferant suchen{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={unifiedSearchTerm}
                        onChange={handleUnifiedSearch}
                        placeholder="Kunde oder Lieferant suchen..."
                        className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                      />
                      {isLoadingUnified && (
                        <div className="absolute right-3 top-2">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                        </div>
                      )}

                      {/* Unified dropdown */}
                      {showUnifiedDropdown && unifiedResults.length > 0 && (
                        <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md max-h-60 overflow-auto border border-gray-200">
                          {unifiedResults.map((item) => (
                            <div
                              key={`${item.type}-${item._id}`}
                              className="cursor-pointer p-2 hover:bg-gray-100"
                              onClick={() => handleUnifiedSelection(item)}
                            >
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <div className="font-medium">
                                    {item.bezeichnung}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    Konto: {item.konto}
                                  </div>
                                </div>
                                <span
                                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                    item.type === "kunde"
                                      ? "bg-blue-100 text-blue-800"
                                      : "bg-green-100 text-green-800"
                                  }`}
                                >
                                  {item.type === "kunde"
                                    ? "Kunde"
                                    : "Lieferant"}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Konto <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="Konto"
                      value={newRechnung.Konto}
                      onChange={handleRechnungInputChange}
                      required
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Datum <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="Datum"
                      value={newRechnung.Datum}
                      onChange={handleRechnungInputChange}
                      required
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Gkto
                    </label>
                    <input
                      type="text"
                      name="Gkto"
                      value={newRechnung.Gkto}
                      onChange={handleRechnungInputChange}
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Buchungstext
                    </label>
                    <input
                      type="text"
                      name="Buchungstext"
                      value={newRechnung.Buchungstext}
                      onChange={handleRechnungInputChange}
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Beleg
                    </label>
                    <input
                      type="text"
                      name="Beleg1"
                      value={newRechnung.Beleg1}
                      onChange={handleRechnungInputChange}
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Betrag <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="Soll"
                      value={newRechnung.Soll}
                      onChange={handleRechnungInputChange}
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      required
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                      style={{
                        appearance: "textfield",
                        MozAppearance: "textfield",
                      }}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      SKONTO (%)
                    </label>
                    <input
                      type="number"
                      name="skontoPercentage"
                      value={newRechnung.skontoPercentage}
                      onChange={handleRechnungInputChange}
                      step="0.01"
                      min="0"
                      max="100"
                      placeholder="0.00"
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                    />
                    {newRechnung.skontoPercentage &&
                      parseFloat(newRechnung.skontoPercentage) > 0 &&
                      newRechnung.originalBetrag && (
                        <div className="text-sm text-gray-600 mt-1 p-2 bg-blue-50 rounded">
                          <div className="flex justify-between">
                            <span>Ursprünglicher Betrag:</span>
                            <span className="font-medium">
                              €
                              {parseFloat(
                                newRechnung.originalBetrag || 0,
                              ).toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>
                              SKONTO Rabatt (
                              {parseFloat(
                                newRechnung.skontoPercentage || 0,
                              ).toFixed(2)}
                              %):
                            </span>
                            <span className="text-red-600 font-medium">
                              -€{parseFloat(newRechnung.skonto || 0).toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between border-t pt-1 mt-1">
                            <span className="font-semibold">
                              Finaler Betrag:
                            </span>
                            <span className="font-semibold text-green-600">
                              €{parseFloat(newRechnung.Soll || 0).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dokumente hochladen
                  </label>
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                    <div className="space-y-1 text-center">
                      {newRechnung.file ? (
                        <div className="flex flex-col items-center">
                          <p className="text-sm text-gray-700">
                            Ausgewählte Datei:{" "}
                            <span className="font-medium">
                              {newRechnung.file.name}
                            </span>
                          </p>
                          <p className="text-xs text-gray-500">
                            ({(newRechnung.file.size / 1024 / 1024).toFixed(2)}{" "}
                            MB)
                          </p>
                          <button
                            type="button"
                            onClick={() =>
                              setNewRechnung((prev) => ({
                                ...prev,
                                file: null,
                              }))
                            }
                            className="mt-2 text-sm text-red-600 hover:text-red-800"
                          >
                            Entfernen
                          </button>
                        </div>
                      ) : (
                        <>
                          <svg
                            className="mx-auto h-12 w-12 text-gray-400"
                            stroke="currentColor"
                            fill="none"
                            viewBox="0 0 48 48"
                            aria-hidden="true"
                          >
                            <path
                              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <div className="flex text-sm text-gray-600">
                            <label
                              htmlFor="file-upload"
                              className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500"
                            >
                              <span>Datei hochladen</span>
                              <input
                                id="file-upload"
                                name="file-upload"
                                type="file"
                                onChange={handleRechnungFileChange}
                                accept="image/*,application/pdf"
                                className="sr-only"
                              />
                            </label>
                            <p className="pl-1">oder hier ablegen</p>
                          </div>
                          <p className="text-xs text-gray-500">
                            PDF, JPG, PNG bis zu 10MB
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </form>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={handleCloseAddRechnungModal}
                disabled={isCreatingRechnung}
                className={`px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800 ${
                  isCreatingRechnung ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                Abbrechen
              </button>
              <button
                onClick={handleCreateRechnung}
                disabled={
                  isCreatingRechnung ||
                  !newRechnung.Konto ||
                  !newRechnung.Datum ||
                  !newRechnung.Soll
                }
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg ${
                  isCreatingRechnung ||
                  !newRechnung.Konto ||
                  !newRechnung.Datum ||
                  !newRechnung.Soll
                    ? "bg-blue-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {isCreatingRechnung ? (
                  <span className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Speichern...
                  </span>
                ) : (
                  "Speichern"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Rechnungs;
